import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  FUND_ERROR_CODES,
  type FundDetailResponse,
  type FundIndexRowWire,
  type FundLikelihood,
  type FundStatus,
  type FundType,
  type ListFundsResponse,
  type SaveFundInput,
  type SaveFundResponse,
  type UpdateFundInput,
  type FundWire,
} from '@releve/shared';
import { useOrganization } from '@/contexts/OrganizationContext';
import { authedGet, authedPost, authedPatch, authedDelete, type EnvelopeError } from '@/lib/api';
import { toast } from '@/hooks/useToast';
import { invalidateProjectMoney } from '@/hooks/useProjectMoneySummary';
import { invalidateARInvoiceCreateCaches } from '@/hooks/useAccountsReceivable';
import { invalidateAuditCaches } from '@/lib/auditCacheKeys';
import i18n from '@/i18n';

/**
 * One fund as the list and detail reads return it: a `fund_index` row (the
 * `funds` row plus the schedule aggregates — `installment_count`,
 * `awarded_amount`, `received_amount`, `pipeline_amount`). The shared
 * `FundIndexRowWire` with its CHECK-constrained text columns narrowed to
 * their unions. `is_active` is generated (`status = 'awarded'`).
 */
export interface Fund extends Omit<FundIndexRowWire, 'fund_type' | 'status' | 'likelihood'> {
  fund_type: FundType;
  status: FundStatus;
  likelihood: FundLikelihood | null;
}

/** The posting-picker rule — see `lib/pickableFunds.ts`. */
export { pickableFunds } from '@/lib/pickableFunds';

const fundsBasePath = (organizationId: string) => `/api/v1/funds/orgs/${organizationId}/funds`;

/** Query keys — everything sits under `['funds', orgId]`, which every fund write invalidates. */
export const fundKeys = {
  all: (organizationId: string | undefined) => ['funds', organizationId] as const,
  list: (organizationId: string | undefined, projectId: string | null) =>
    ['funds', organizationId, { projectId }] as const,
  detail: (organizationId: string | undefined, code: string | undefined) =>
    ['funds', organizationId, 'detail', code] as const,
};

/**
 * The org's funds (fund_code-ordered `fund_index` rows), read through the
 * Hono backend (plain RLS-scoped read — see
 * apps/api/src/routes/accounting/funds.ts and docs/BACKEND_API.md →
 * "Read endpoints").
 *
 * Key sits under the `['funds', organizationId]` prefix the fund mutations
 * below invalidate; the options slot is normalized (null for "absent") so
 * surfaces that omit `projectId` share one cache entry.
 */
export const useFunds = (organizationId?: string, options?: { projectId?: string }) => {
  const projectId = options?.projectId ?? null;

  return useQuery({
    queryKey: fundKeys.list(organizationId, projectId),
    queryFn: async (): Promise<Fund[]> => {
      if (!organizationId) return [];

      const params = new URLSearchParams();
      if (projectId) params.set('projectId', projectId);
      const qs = params.toString();

      const result = await authedGet<ListFundsResponse>(
        `${fundsBasePath(organizationId)}${qs ? `?${qs}` : ''}`,
      );
      // The text columns are DB CHECK-enforced to the narrowed unions.
      return result.funds as Fund[];
    },
    enabled: !!organizationId,
    // Reference data (CLAUDE.md): avoid a refetch on every consumer mount.
    staleTime: 5 * 60_000,
  });
};

/** The fund detail read: the `fund_index` row by code + its installments (by sequence). */
export interface FundDetail {
  fund: Fund;
  installments: FundDetailResponse['installments'];
}

/**
 * One fund by its code (`GET …/funds/:code`, gated on the `fund_management`
 * flag server-side). A missing or other-org code is a 404.
 */
export const useFundDetail = (code: string | undefined) => {
  const { selectedOrganization } = useOrganization();
  const organizationId = selectedOrganization?.id;

  return useQuery({
    queryKey: fundKeys.detail(organizationId, code),
    queryFn: async (): Promise<FundDetail> => {
      const result = await authedGet<FundDetailResponse>(
        `${fundsBasePath(organizationId!)}/${encodeURIComponent(code!)}`,
      );
      return { fund: result.fund as Fund, installments: result.installments };
    },
    enabled: !!organizationId && !!code,
    staleTime: 60_000,
  });
};

/**
 * The translated message for a fund write failure. `save_fund`'s refusals
 * arrive as `{ error: <code>, code: <code> }` (`FUND_ERROR_CODES`); each has
 * a `funds.errors.<CODE>` string. Anything else (a plain 400 such as
 * `FUND_AWARD_FAILED: …`, a network error) shows its own message.
 */
export const fundErrorMessage = (error: unknown): string => {
  const envelopeCode = (error as EnvelopeError<{ code?: string }> | null | undefined)?.envelope?.code;
  const message = error instanceof Error ? error.message : String(error);
  const code = envelopeCode ?? message;
  if ((FUND_ERROR_CODES as readonly string[]).includes(code)) {
    return i18n.t(`funds.errors.${code}`);
  }
  return message;
};

/** Refusals are workflow rules, not bugs: App.tsx's MutationCache.onError skips Sentry for them. */
const FUND_MUTATION_META = { expectedErrorCodes: FUND_ERROR_CODES as readonly string[] } as const;

/**
 * Every cache a fund save changes: the fund reads, the project money summary
 * (Funding secured / pipeline), the History panels — and, when the save was
 * an award, everything a new AR invoice changes (one invoice per installment).
 */
const invalidateFundSaveCaches = (
  queryClient: QueryClient,
  organizationId: string,
  result?: Pick<SaveFundResponse, 'invoices'>,
) => {
  void queryClient.invalidateQueries({ queryKey: fundKeys.all(organizationId) });
  invalidateProjectMoney(queryClient, organizationId);
  invalidateAuditCaches(queryClient, organizationId);
  if (result && result.invoices.length > 0) {
    invalidateARInvoiceCreateCaches(queryClient, organizationId);
  }
};

/**
 * Fund writes go through the Hono backend: create / update / award are all
 * `save_fund` (one RPC — header, installment schedule, award), DELETE is
 * Pattern B. See apps/api/src/routes/accounting/funds.ts and
 * docs/BACKEND_API.md. Saves answer `SaveFundResponse` (`{ fund, invoices }`).
 */
export const useCreateFund = () => {
  const queryClient = useQueryClient();

  return useMutation({
    meta: FUND_MUTATION_META,
    mutationFn: async ({ organizationId, data }: { organizationId: string; data: SaveFundInput }) =>
      authedPost<SaveFundResponse>(fundsBasePath(organizationId), data),
    onSuccess: (result, { organizationId }) => {
      invalidateFundSaveCaches(queryClient, organizationId, result);
      toast.success(i18n.t('funds.toasts.created', { name: result.fund.fund_name }));
    },
    onError: (error) => {
      toast.error(fundErrorMessage(error));
    },
  });
};

/**
 * Update keeps its `{ id }`-only interface; the org for the org-scoped API
 * path comes from OrganizationContext. `save_fund` pins the fund to that org,
 * so a mismatched fund id fails closed (FUND_NOT_FOUND, no cross-org write).
 *
 * The same PATCH is the award: a body carrying `award_date` (plus, from the
 * "Mark as awarded" form, header fields and the final schedule) makes
 * `save_fund` post one AR invoice to the funder per installment, numbered
 * `<fund_code>/<sequence>`, and flip the fund to `awarded` — all or nothing.
 * Only the success toast tells the two apart.
 */
export const useUpdateFund = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    meta: FUND_MUTATION_META,
    mutationFn: async ({ id, data }: { id: string; data: UpdateFundInput }) => {
      if (!selectedOrganization?.id) throw new Error('No organization selected');
      return authedPatch<SaveFundResponse>(`${fundsBasePath(selectedOrganization.id)}/${id}`, data);
    },
    onSuccess: (result, { data }) => {
      invalidateFundSaveCaches(queryClient, result.fund.organization_id, result);
      toast.success(
        data.award_date
          ? i18n.t('funds.toasts.awarded', { name: result.fund.fund_name, count: result.invoices.length })
          : i18n.t('funds.toasts.updated'),
      );
    },
    onError: (error) => {
      toast.error(fundErrorMessage(error));
    },
  });
};

/**
 * True when a fund delete failed because ledger data still references the
 * fund — the DELETE fails closed on the FK and the API surfaces Postgres's
 * `violates foreign key constraint` message verbatim. Callers use this to
 * swap the raw-SQL toast for instructional UI (close the fund instead).
 */
export const isFundInUseError = (error: unknown): boolean =>
  error instanceof Error && /foreign key/i.test(error.message);

export const useDeleteFund = () => {
  const queryClient = useQueryClient();
  const { selectedOrganization } = useOrganization();

  return useMutation({
    mutationFn: async (id: string) => {
      if (!selectedOrganization?.id) throw new Error('No organization selected');
      // The API returns the deleted `funds` row (organization_id + fund_name).
      return authedDelete<FundWire>(`${fundsBasePath(selectedOrganization.id)}/${id}`);
    },
    onSuccess: (fund) => {
      // Deleting a fund also drops its project's Funding secured / pipeline figure.
      invalidateFundSaveCaches(queryClient, fund.organization_id);
      toast.success(i18n.t('funds.toasts.deleted', { name: fund.fund_name }));
    },
    onError: (error) => {
      // The fund-in-use failure gets an instructional dialog from the caller
      // (FundsTab, via a per-mutate onError) instead of a raw-SQL toast.
      if (isFundInUseError(error)) return;
      toast.error(error.message);
    },
  });
};
